import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDrawer } from '../../components/DrawerContext.jsx';
import Drawer from "react-modern-drawer";
import {
  FaPenToSquare, FaUserPen, FaUserPlus, FaCheck, FaX
} from "react-icons/fa6";
import DealerForm from "./DealerForm.jsx";
import { useAuth } from '../login/AuthContext';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  Email, Phone, LocationOn, Business, PictureAsPdf, Archive,
  Description as DescriptionIcon, InsertDriveFile, TableChart,
  Article, Image, People, ShoppingCart, Add as AddIcon,
  Edit,
  Delete,
  Close,
  CheckCircle,
  Cancel
} from "@mui/icons-material";
import {
  Grid, Paper, Typography, Tabs, Tab, Box, Divider, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  CircularProgress, Button, IconButton, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip
} from "@mui/material";
import { toast } from "react-toastify";
import config from "../../config/config";
import UserManagementForm from './UserManagementForm';
import Loading from "../../components/Loading/Loading.jsx";

const DealerDetails = () => {
  const { userData, token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [dealer, setDealer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dealerFiles, setDealerFiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("musteriler");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Infinite scroll için state'ler - Orders
  const [ordersPage, setOrdersPage] = useState(1);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const ordersObserver = useRef();

  // Infinite scroll için state'ler - Customers
  const [customersPage, setCustomersPage] = useState(1);
  const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(false);
  const customersObserver = useRef();
  const [filesLoading, setFilesLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const { openDrawerWithDealer, isDrawerOpen, toggleDrawer, editingDealer } = useDrawer();

  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDeleteDialogOpen, setUserDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Modern Tema Renkleri
  const modernTheme = {
    primary: "#1C355E",
    primaryLight: "#E8EAF6",
    accent: "#00A7B5",
    accentHover: "#008B98",
    success: "#098f69",
    warning: "#F59E0B",
    error: "#FF4343",
    text: {
      primary: "#1F2937",
      secondary: "#6B7280",
      light: "#9CA3AF",
    },
    background: {
      main: "#F9FAFB",
      white: "#FFFFFF",
      paper: "#FFFFFF",
    },
    border: "#E5E7EB",
    borderLight: "#F3F4F6",
  };


  // Son sipariş elementi için referans
  const lastOrderElementRef = useCallback(node => {
    if (ordersLoading) return;
    if (ordersObserver.current) ordersObserver.current.disconnect();

    ordersObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreOrders) {
        setOrdersPage(prevPage => prevPage + 1);
      }
    }, {
      threshold: 0.1,
      rootMargin: '20px'
    });

    if (node) ordersObserver.current.observe(node);
  }, [ordersLoading, hasMoreOrders]);

  // Son müşteri elementi için referans
  const lastCustomerElementRef = useCallback(node => {
    if (customersLoading) return;
    if (customersObserver.current) customersObserver.current.disconnect();

    customersObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreCustomers) {
        setCustomersPage(prevPage => prevPage + 1);
      }
    }, {
      threshold: 0.1,
      rootMargin: '20px'
    });

    if (node) customersObserver.current.observe(node);
  }, [customersLoading, hasMoreCustomers]);

  // Siparişleri getir
  const fetchDealerOrders = async (dealerId, page = 1) => {
    if (!dealerId) return;

    setOrdersLoading(true);
    try {
      const response = await fetch(
        `${config.baseURL}/bayiler/${dealerId}/siparisler?page=${page}&limit=20`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Siparişler alınamadı");
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Siparişler alınamadı");
      }

      const newOrders = data.data;
      setHasMoreOrders(data.hasMore);

      const detailedOrders = await Promise.all(
        newOrders.map(async (order) => {
          try {
            const detailResponse = await fetch(
              `${config.baseURL}/siparis/${order.Id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (!detailResponse.ok) {
              throw new Error(`Sipariş detayı alınamadı: ${order.Id}`);
            }

            const detailData = await detailResponse.json();
            return {
              ...order,
              details: detailData,
            };
          } catch (err) {
            console.error(`Sipariş ${order.Id} detayı alınırken hata:`, err);
            return { ...order, details: null };
          }
        })
      );

      setOrders(prev => page === 1 ? detailedOrders : [...prev, ...detailedOrders]);
    } catch (err) {
      toast.error("Siparişler yüklenirken hata oluştu: " + err.message);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Müşterileri getir
  const fetchDealerCustomers = async (dealerId, page = 1) => {
    if (!dealerId) return;

    setCustomersLoading(true);
    try {
      const response = await fetch(
        `${config.baseURL}/bayiler/${dealerId}/musteriler?page=${page}&limit=20`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Müşteriler alınamadı");
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Müşteriler alınamadı");
      }

      const newCustomers = data.data;
      setHasMoreCustomers(data.hasMore);

      setCustomers(prev => page === 1 ? newCustomers : [...prev, ...newCustomers]);
    } catch (err) {
      toast.error("Müşteriler yüklenirken hata oluştu: " + err.message);
    } finally {
      setCustomersLoading(false);
      setLoading(false);
    }
  };

  // Dosyaları getir
  const fetchDealerFiles = async (dealerId) => {
    setFilesLoading(true);
    try {
      const response = await fetch(`${config.baseURL}/musteriler/musteri-dokuman/${dealerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Dökümanlar alınamadı");
      const data = await response.json();

      const files = data?.map((doc) => ({
        dokumanId: doc.Id,
        name: doc.DosyaAdi,
        systemName: doc.SistemDosyaAdi,
        url: `${config.baseURL}/docs/${doc.SistemDosyaAdi}`,
        type: `application/${doc.DosyaAdi.split(".").pop()}`,
        size: doc.DosyaBoyutu,
        icon: getFileIcon(`application/${doc.DosyaAdi.split(".").pop()}`),
      }));

      setDealerFiles(files || []);
    } catch (err) {
      toast.error("Dökümanlar yüklenirken hata oluştu");
    } finally {
      setFilesLoading(false);
    }
  };

  // Kullanıcıları getir
  const fetchDealerUsers = async (dealerId) => {
    setUsersLoading(true);
    try {
      const response = await fetch(`${config.baseURL}/bayiler/${dealerId}/kullanicilar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Kullanıcılar alınamadı");
      const data = await response.json();
      setUsers(data.data || []);
    } catch (err) {
      toast.error("Kullanıcılar yüklenirken hata oluştu");
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserDrawerOpen = (user = null) => {
    setSelectedUser(user);
    setUserDrawerOpen(true);
  };

  const handleUserDrawerClose = () => {
    setUserDrawerOpen(false);
    setSelectedUser(null);
  };

  const handleUserDeleteClick = (user) => {
    setUserToDelete(user.Id);
    setUserDeleteDialogOpen(true);
  };

  const handleUserDeleteConfirm = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${config.baseURL}/kullanici/kullanicilar/${userToDelete}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Kullanıcı başarıyla silindi');
        await fetchDealerUsers(id);
      } else {
        toast.error(data.message || 'Kullanıcı silinirken hata oluştu');
      }
    } catch (error) {
      toast.error('Kullanıcı silinirken hata oluştu');
    } finally {
      setIsProcessing(false);
      setUserDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleUserSave = async (userData) => {
    try {
      if (selectedUser) {
        const response = await fetch(`${config.baseURL}/kullanici/${selectedUser.Id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...userData,
            Id: selectedUser.Id,
            BayiId: selectedUser.BayiId
          }),
        });
        const data = await response.json();
        if (data.success) {
          toast.success('Kullanıcı başarıyla güncellendi');
          handleUserDrawerClose();
          await fetchDealerUsers(id);
        } else {
          toast.error(data.message || 'Kullanıcı güncellenirken hata oluştu');
        }
      } else {
        const response = await fetch(`${config.baseURL}/kullanici/bayiler/${id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(userData),
        });
        const data = await response.json();
        if (data.success) {
          toast.success('Kullanıcı başarıyla eklendi');
          handleUserDrawerClose();
          await fetchDealerUsers(id);
        } else {
          toast.error(data.message || 'Kullanıcı eklenirken hata oluştu');
        }
      }
    } catch (error) {
      toast.error('İşlem sırasında bir hata oluştu');
    }
  };

  const handleUserToggleStatus = async (userId, newStatus) => {
    try {
      const response = await fetch(`${config.baseURL}/kullanici/kullanicilar/${userId}/deactivate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ Etkin: newStatus }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Kullanıcı ${newStatus ? 'aktif' : 'pasif'} duruma alındı`);
        await fetchDealerUsers(id);
      } else {
        toast.error(data.message || 'Durum değiştirme işlemi sırasında bir hata oluştu');
      }
    } catch (error) {
      toast.error('Durum değiştirme işlemi sırasında bir hata oluştu');
    }
  };

  const getYetkiAdi = (kod) => {
    switch (kod) {
      case '1': return 'Admin';
      case '2': return 'User';
      case '3': return 'Viewer';
      default: return kod;
    }
  };


  // İlk yükleme
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${config.baseURL}/bayiler/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          navigate("/404", { replace: true });;
          return;
        }

        const data = await response.json();
        setDealer(data.data);
        if (data.data.Id) {
          fetchDealerOrders(data.data.Id, 1);
          fetchDealerCustomers(data.data.Id, 1);
          fetchDealerFiles(data.data.MusteriKodu);
          fetchDealerUsers(data.data.Id);
        }
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, token, navigate]);

  // Sayfa değişiminde siparişleri getir
  useEffect(() => {
    if (dealer?.Id && ordersPage > 1) {
      fetchDealerOrders(dealer.Id, ordersPage);
    }
  }, [ordersPage, dealer?.Id]);

  // Sayfa değişiminde müşterileri getir
  useEffect(() => {
    if (dealer?.Id && customersPage > 1) {
      fetchDealerCustomers(dealer.Id, customersPage);
    }
  }, [customersPage, dealer?.Id]);

  // Tab değiştiğinde sayfa numaralarını sıfırla
  useEffect(() => {
    if (activeTab === "musteriler") {
      setCustomersPage(1);
      setHasMoreCustomers(true);
      if (dealer?.Id) {
        fetchDealerCustomers(dealer.Id, 1);
      }
    }
  }, [activeTab]);

  // Diğer Handler'lar
  const handleEdit = async () => {
    try {
      const response = await axios.get(`${config.baseURL}/bayiler/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      openDrawerWithDealer(response.data.data);
    } catch (error) {
      toast.error("Bayi verileri alınamadı");
    }
  };

  const handleDealerCreated = (newDealer) => {
    // Sadece mevcut bayi bilgilerini güncelle
    const updateDealer = async () => {
      try {
        const response = await fetch(`${config.baseURL}/bayiler/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error('Bayi bilgileri alınamadı');
        }

        const data = await response.json();
        setDealer(data.data);
      } catch (error) {
        toast.error("Bayi bilgileri güncellenirken hata oluştu");
      }
    };

    updateDealer();
    toggleDrawer();
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files.length) return;

    setUploading(true);
    const formData = new FormData();

    Array.from(files).forEach(file => {
      formData.append('customerFiles', file);
    });

    try {
      const response = await axios.post(
        `${config.baseURL}/musteriler/musteri-dokuman/upload-document/${dealer.MusteriKodu}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      if (response.data.success) {
        toast.success('Dökümanlar başarıyla yüklendi');
        fetchDealerFiles(dealer.MusteriKodu);
      }
    } catch (error) {
      toast.error('Döküman yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadFile = (fileName) => {
    const fileUrl = `${config.baseURL}/musteriler/musteri-dokuman/download/${fileName}`;
    fetch(fileUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Dosya indirilemedi.");
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Download error:", error);
        toast.error("Dosya indirilemedi");
      });
  };

  // Yardımcı fonksiyonlar
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR');
  };

  // ... önceki kodun devamı

  const getStatusColor = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
      case 'onaylandı':
      case 'aktif':
      case 'kullanımda':
        return 'success';
      case 'onay bekliyor':
        return 'warning';
      case 'reddedildi':
      case 'pasif':
        return 'error';
      default:
        return 'default';
    }
  };

  const getFileIcon = (type = "") => {
    if (type.includes("pdf")) return <PictureAsPdf color="error" />;
    if (type.includes("zip")) return <Archive color="primary" />;
    if (type.includes("xlsx")) return <TableChart color="success" />;
    if (type.includes("xml")) return <DescriptionIcon color="info" />;
    if (type.includes("docs")) return <Article color="info" />;
    if (type.includes("png") || type.includes("jpg")) return <Image color="warning" />;
    return <InsertDriveFile color="disabled" />;
  };

  const formatFileSize = (size) => {
    if (size < 1024) return `${size} B`;
    if (size < 1048576) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / 1048576).toFixed(2)} MB`;
  };

  // Loading ve error durumları
  if (error) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        p={2}
        sx={{ bgcolor: modernTheme.background.main }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 400,
            borderRadius: 3,
            border: `1px solid ${modernTheme.error}`,
            bgcolor: modernTheme.background.white
          }}
        >
          <Typography
            color="error"
            align="center"
            variant="h6"
            fontWeight={600}
            sx={{ mb: 1 }}
          >
            ⚠️ Hata Oluştu
          </Typography>
          <Typography color="text.secondary" align="center">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!dealer || loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ bgcolor: modernTheme.background.main }}
      >
        <Loading />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: modernTheme.background.main, minHeight: "100vh" }}>
      <Grid container spacing={3}>
        {/* Sol Panel - Bayi Bilgileri */}
        <Grid item xs={12} sm={12} md={4} lg={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: "calc(100vh - 48px)",
              borderRadius: 3,
              border: `1px solid ${modernTheme.border}`,
              bgcolor: modernTheme.background.white,
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: modernTheme.background.main,
                      color: modernTheme.text.secondary
                    }}
                  >
                    <Business />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "text.secondary" }}>
                    Bayi Bilgileri
                  </Typography>
                </Box>
                <Button
                  startIcon={<FaPenToSquare />}
                  variant="outlined"
                  size="small"
                  onClick={handleEdit}
                  sx={{
                    borderColor: modernTheme.border,
                    color: modernTheme.text.secondary,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: modernTheme.accent,
                      bgcolor: "rgba(0, 167, 181, 0.04)",
                      color: modernTheme.accent
                    }
                  }}
                >
                  Düzenle
                </Button>
              </Box>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: modernTheme.text.primary }}>
                {dealer.BayiAdi}
              </Typography>
              <Chip
                label={`ID: ${dealer.Kodu}`}
                size="small"
                sx={{
                  bgcolor: modernTheme.background.main,
                  color: modernTheme.text.secondary,
                  fontWeight: 600,
                  border: `1px solid ${modernTheme.border}`
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: modernTheme.text.secondary, textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                İletişim Bilgileri
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: modernTheme.background.main,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: modernTheme.primaryLight
                  }
                }}
              >
                <Email fontSize="small" color="action" />
                <Typography variant="body2" sx={{ color: modernTheme.text.primary }}>{dealer.EPosta || "Belirtilmemiş"}</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: modernTheme.background.main,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: modernTheme.primaryLight
                  }
                }}
              >
                <Phone fontSize="small" color="action" />
                <Typography variant="body2" sx={{ color: modernTheme.text.primary }}>{dealer.Tel || "Belirtilmemiş"}</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: modernTheme.background.main,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: modernTheme.primaryLight
                  }
                }}
              >
                <LocationOn fontSize="small" color="action" sx={{ mt: 0.3 }} />
                <Typography variant="body2" sx={{ color: modernTheme.text.primary }}>{dealer.Adres || "Adres belirtilmemiş"}</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: modernTheme.background.main,
                  border: `1px solid ${modernTheme.borderLight}`
                }}
              >
                <Typography variant="caption" sx={{ color: modernTheme.text.secondary, textTransform: "uppercase", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                  Üst Bayi
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: modernTheme.text.primary }}>
                  {dealer.UstBayiAdi || "Ana Bayi"}
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: modernTheme.background.main,
                  border: `1px solid ${modernTheme.borderLight}`
                }}
              >
                <Typography variant="caption" sx={{ color: modernTheme.text.secondary, textTransform: "uppercase", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                  Bayi Türü
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: modernTheme.text.primary }}>
                  {dealer.BayiTuru || "Belirtilmemiş"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Sağ Panel - Sekmeler */}
        <Grid item xs={12} sm={12} md={8} lg={8}>
          <Paper
            elevation={0}
            sx={{
              height: "calc(100vh - 48px)",
              borderRadius: 3,
              border: `1px solid ${modernTheme.border}`,
              bgcolor: modernTheme.background.white,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Box sx={{
              borderBottom: 1,
              borderColor: modernTheme.border,
              bgcolor: modernTheme.background.main,
              boxShadow: `0 1px 3px rgba(0, 0, 0, 0.05)`
            }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
                sx={{
                  px: 2,
                  '& .MuiTab-root': {
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    minHeight: 56,
                    color: modernTheme.text.secondary,
                    transition: "all 0.2s ease",
                    '&:hover': {
                      color: modernTheme.accent,
                      bgcolor: "rgba(0, 167, 181, 0.04)"
                    },
                    '&.Mui-selected': {
                      color: modernTheme.accent,
                      fontWeight: 600
                    }
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                    bgcolor: modernTheme.accent
                  }
                }}
              >
                <Tab
                  icon={<Business />}
                  iconPosition="start"
                  label="Müşteriler"
                  value="musteriler"
                />
                <Tab
                  icon={<ShoppingCart />}
                  iconPosition="start"
                  label="Siparişler"
                  value="siparisler"
                />
                <Tab
                  icon={<DescriptionIcon />}
                  iconPosition="start"
                  label="Dökümanlar"
                  value="dokumanlar"
                />
                <Tab
                  icon={<People />}
                  iconPosition="start"
                  label="Kullanıcılar"
                  value="kullanicilar"
                />
              </Tabs>
            </Box>

            <Box
              sx={{
                p: 3,
                flex: 1,
                overflowY: "auto",
                scrollBehavior: "smooth",
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: modernTheme.borderLight,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: modernTheme.border,
                  borderRadius: '4px',
                  '&:hover': {
                    background: modernTheme.text.light,
                  },
                },
              }}
            >
              {/* Siparişler Tablosu */}
              {activeTab === "siparisler" && (
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          Sipariş No
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          Tarih
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          Paket
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          Ek Modüller
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          Durum
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.map((order, index) => (
                        <TableRow
                          key={order.Id}
                          ref={index === orders.length - 1 ? lastOrderElementRef : null}
                          onClick={() => navigate(`/siparis/${order.Id}`)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}
                        >
                          <TableCell>{order.Id}</TableCell>
                          <TableCell>{formatDate(order.Tarih)}</TableCell>
                          <TableCell>
                            {order.details?.Hareketler.find(
                              (hareket) => hareket.EkModul === false
                            )?.PaketAdi || "Paket adı bulunamadı"}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {order.details?.Hareketler
                                .filter((hareket) => hareket.EkModul === true)
                                .map((hareket, index) => (
                                  <Chip
                                    key={index}
                                    label={hareket.name}
                                    size="small"
                                    sx={{
                                      bgcolor: modernTheme.background.main,
                                      color: modernTheme.text.secondary,
                                      fontWeight: 500,
                                      fontSize: "0.8125rem",
                                      height: "26px",
                                      border: `1px solid ${modernTheme.border}`
                                    }}
                                  />
                                ))}
                              {order.details?.Hareketler.filter((h) => h.EkModul === true).length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                  Ek Modül Bulunmuyor
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.DurumAd}
                              color={getStatusColor(order.DurumAd)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {ordersLoading && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                              <CircularProgress />
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                      {!ordersLoading && orders.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Henüz sipariş verisi yok
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Müşteriler Tablosu */}
              {activeTab === "musteriler" && (
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          Müşteri No
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          Ünvan
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          Yetkili
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          Telefon
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: modernTheme.background.main,
                          fontWeight: 700,
                          color: modernTheme.text.primary,
                          borderBottom: `2px solid ${modernTheme.border}`
                        }}>
                          E-posta
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customers.map((customer, index) => (
                        <TableRow
                          key={customer.Id}
                          ref={index === customers.length - 1 ? lastCustomerElementRef : null}
                          onClick={() => navigate(`/musteriler/${customer.Id}`)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}
                        >
                          <TableCell>{customer.MusteriNo}</TableCell>
                          <TableCell sx={{
                            maxWidth: 200,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>{customer.Unvan}</TableCell>
                          <TableCell>{customer.Yetkili || "-"}</TableCell>
                          <TableCell>{customer.Tel || "-"}</TableCell>
                          <TableCell>{customer.EPosta || "-"}</TableCell>
                        </TableRow>
                      ))}
                      {customersLoading && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                              <CircularProgress />
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                      {!customersLoading && customers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Henüz müşteri verisi yok
                          </TableCell>
                        </TableRow>
                      )}
                      {!hasMoreCustomers && customers.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="body2" sx={{ color: modernTheme.text.secondary, p: 2 }}>
                              Tüm kayıtlar yüklendi
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Dökümanlar Tablosu */}
              {activeTab === "dokumanlar" && (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{
                            bgcolor: modernTheme.background.main,
                            fontWeight: 700,
                            color: modernTheme.text.primary,
                            borderBottom: `2px solid ${modernTheme.border}`
                          }}>
                            Dosya Adı
                          </TableCell>
                          <TableCell sx={{
                            bgcolor: modernTheme.background.main,
                            fontWeight: 700,
                            color: modernTheme.text.primary,
                            borderBottom: `2px solid ${modernTheme.border}`
                          }}>
                            Boyut
                          </TableCell>
                          <TableCell sx={{
                            bgcolor: modernTheme.background.main,
                            fontWeight: 700,
                            color: modernTheme.text.primary,
                            borderBottom: `2px solid ${modernTheme.border}`
                          }}>
                            İşlemler
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filesLoading ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : dealerFiles.length > 0 ? (
                          dealerFiles.map((file) => (
                            <TableRow key={file.dokumanId}>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  {getFileIcon(file.type)}
                                  <Typography variant="body1" sx={{ ml: 2 }}>
                                    {file.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{formatFileSize(file.size)}</TableCell>
                              <TableCell>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleDownloadFile(file.systemName)}
                                >
                                  <FileDownloadIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              Henüz döküman verisi yok
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Kullanıcılar Tablosu */}
              {activeTab === "kullanicilar" && (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{
                            bgcolor: modernTheme.background.main,
                            fontWeight: 700,
                            color: modernTheme.text.primary,
                            borderBottom: `2px solid ${modernTheme.border}`
                          }}>
                            Ad Soyad
                          </TableCell>
                          <TableCell sx={{
                            bgcolor: modernTheme.background.main,
                            fontWeight: 700,
                            color: modernTheme.text.primary,
                            borderBottom: `2px solid ${modernTheme.border}`
                          }}>
                            Kullanıcı Adı
                          </TableCell>
                          <TableCell sx={{
                            bgcolor: modernTheme.background.main,
                            fontWeight: 700,
                            color: modernTheme.text.primary,
                            borderBottom: `2px solid ${modernTheme.border}`
                          }}>
                            E-posta
                          </TableCell>
                          <TableCell sx={{
                            bgcolor: modernTheme.background.main,
                            fontWeight: 700,
                            color: modernTheme.text.primary,
                            borderBottom: `2px solid ${modernTheme.border}`
                          }}>
                            Yetki Seviyesi
                          </TableCell>
                          <TableCell sx={{
                            bgcolor: modernTheme.background.main,
                            fontWeight: 700,
                            color: modernTheme.text.primary,
                            borderBottom: `2px solid ${modernTheme.border}`
                          }}>
                            Durum
                          </TableCell>
                          <TableCell sx={{
                            bgcolor: modernTheme.background.main,
                            fontWeight: 700,
                            color: modernTheme.text.primary,
                            borderBottom: `2px solid ${modernTheme.border}`
                          }}>
                            İşlemler
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usersLoading ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : users.length > 0 ? (
                          users.map((user) => (
                            <TableRow key={user.Id}>
                              <TableCell>
                                {`${user.Adi || ''} ${user.Soyadi || ''}`}
                              </TableCell>
                              <TableCell>{user.KullaniciAdi}</TableCell>
                              <TableCell>{user.EPosta}</TableCell>
                              <TableCell>
                                <Chip
                                  label={getYetkiAdi(user.YetkiKodu)}
                                  size="small"
                                  sx={{
                                    bgcolor: modernTheme.background.main,
                                    color: modernTheme.text.secondary,
                                    border: `1px solid ${modernTheme.border}`,
                                    fontWeight: 500
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={user.Etkin ? "Aktif" : "Pasif"}
                                  size="small"
                                  sx={{
                                    bgcolor: user.Etkin ? modernTheme.success : modernTheme.error,
                                    color: "#FFFFFF",
                                    fontWeight: 500
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Tooltip title={user.Etkin ? "Pasife Al" : "Aktif Et"}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUserToggleStatus(user.Id, !user.Etkin);
                                      }}
                                      sx={{
                                        color: user.Etkin ? modernTheme.success : modernTheme.error,
                                        '&:hover': {
                                          bgcolor: user.Etkin
                                            ? `${modernTheme.success}15`
                                            : `${modernTheme.error}15`
                                        }
                                      }}
                                    >
                                      {user.Etkin ? <CheckCircle /> : <Cancel />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Düzenle">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUserDrawerOpen(user);
                                      }}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Sil">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUserDeleteClick(user);
                                      }}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              Henüz kullanıcı bulunmuyor
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Kullanıcı Silme Dialog */}
                  <Dialog
                    open={userDeleteDialogOpen}
                    onClose={() => !isProcessing && setUserDeleteDialogOpen(false)}
                  >
                    <DialogTitle>Kullanıcı Silme Onayı</DialogTitle>
                    <DialogContent>
                      <Typography>
                        Bu kullanıcıyı silmek istediğinizden emin misiniz?
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={() => setUserDeleteDialogOpen(false)}
                        color="primary"
                        disabled={isProcessing}
                        sx={{ textTransform: "none" }}
                      >
                        İptal
                      </Button>
                      <Button
                        onClick={handleUserDeleteConfirm}
                        color="error"
                        disabled={isProcessing}
                        startIcon={isProcessing ? <CircularProgress size={20} /> : null}
                        sx={{ textTransform: "none" }}
                      >
                        {isProcessing ? "Siliniyor..." : "Sil"}
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {/* Kullanıcı Form Drawer */}
                  <Drawer
                    open={userDrawerOpen}
                    onClose={handleUserDrawerClose}
                    direction="right"
                    className="custom-drawer"
                    duration={120}
                    zIndex={600}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        overflowY: 'auto',
                        bgcolor: 'background.default',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          borderBottom: 1,
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: '1rem'
                            }}
                          >
                            {selectedUser ? (
                              <>
                                <Edit style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                                Kullanıcı Düzenle
                              </>
                            ) : (
                              <>
                                <AddIcon style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                                Yeni Kullanıcı Ekle
                              </>
                            )}
                          </Typography>
                          <Box display="flex" gap={0.5}>
                            <Button
                              type="submit"
                              form="usermanagementform"
                              variant="text"
                              size="medium"
                              sx={{
                                minWidth: 'auto',
                                p: 1,
                                color: 'text.secondary',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                  color: 'text.primary'
                                }
                              }}
                            >
                              <FaCheck style={{ fontSize: "18px" }} />
                            </Button>
                            <Button
                              variant="text"
                              size="medium"
                              onClick={handleUserDrawerClose}
                              sx={{
                                minWidth: 'auto',
                                p: 1,
                                color: 'text.secondary',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                  color: 'text.primary'
                                }
                              }}
                            >
                              <FaX style={{ fontSize: "18px" }} />
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, p: 2.5, pt: 2 }}>
                        <UserManagementForm
                          user={selectedUser}
                          onClose={handleUserDrawerClose}
                          onSave={handleUserSave}
                        />
                      </Box>
                    </Box>
                  </Drawer>
                </>
              )}
            </Box>

            {/* Action Button - Kullanıcılar Tab */}
            {activeTab === "kullanicilar" && (
              <Box sx={{
                p: 2.5,
                borderTop: 1,
                borderColor: modernTheme.border,
                bgcolor: modernTheme.background.main,
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleUserDrawerOpen()}
                  sx={{
                    bgcolor: modernTheme.accent,
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: `0 4px 12px rgba(0, 167, 181, 0.3)`,
                    "&:hover": {
                      bgcolor: modernTheme.accentHover,
                      boxShadow: `0 6px 16px rgba(0, 167, 181, 0.4)`,
                      transform: "translateY(-1px)"
                    },
                    transition: "all 0.2s ease"
                  }}
                >
                  Yeni Kullanıcı
                </Button>
              </Box>
            )}

            {/* Action Button - Dökümanlar Tab */}

            {/* Action Button - Dökümanlar Tab */}
            {activeTab === "dokumanlar" && (
              <Box sx={{
                p: 2.5,
                borderTop: 1,
                borderColor: modernTheme.border,
                bgcolor: modernTheme.background.main,
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                  sx={{
                    bgcolor: modernTheme.accent,
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: `0 4px 12px rgba(0, 167, 181, 0.3)`,
                    "&:hover": {
                      bgcolor: modernTheme.accentHover,
                      boxShadow: `0 6px 16px rgba(0, 167, 181, 0.4)`,
                      transform: "translateY(-1px)"
                    },
                    transition: "all 0.2s ease"
                  }}
                >
                  {uploading ? 'Yükleniyor...' : 'Döküman Yükle'}
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={handleFileUpload}
                  />
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Drawer */}
      <Drawer
        open={isDrawerOpen}
        onClose={toggleDrawer}
        direction="right"
        className="custom-drawer"
        duration={120}
        zIndex={600}
      >
        <Box
          sx={{
            height: '100%',
            overflowY: 'auto',
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '1rem'
                }}
              >
                {editingDealer ? (
                  <FaUserPen style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                ) : (
                  <FaUserPlus style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                )}
                {editingDealer ? "Bayi Düzenle" : "Yeni Bayi Ekle"}
              </Typography>
              <Box display="flex" gap={0.5}>
                <Button
                  type="submit"
                  form="bayikaydetform"
                  variant="text"
                  size="medium"
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary'
                    }
                  }}
                >
                  <FaCheck style={{ fontSize: "18px" }} />
                </Button>
                <Button
                  variant="text"
                  size="medium"
                  onClick={toggleDrawer}
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary'
                    }
                  }}
                >
                  <FaX style={{ fontSize: "18px" }} />
                </Button>
              </Box>
            </Box>
          </Box>
          <Box sx={{ flex: 1, p: 2.5, pt: 2 }}>
            <DealerForm editingDealer={editingDealer} onDealerCreated={handleDealerCreated} />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DealerDetails;